/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { readString } from 'react-papaparse';
import { groupBy, uniqueId } from 'lodash';

type Location = {
    Id: string;
    StoreName: string;
    StorePurpose: string;
    StoreDescription: string;
    StreetAddress: string;
    City: string;
    State: string;
    Zip: string;
    Country: string;
    Phones: Record<'Type' | 'Value', string>[];
    Contacts: Record<'Name' | 'Title', string>[];
    Hours: StoreHours;
    SquareFootage: number;
    Longitude: number;
    Latitude: number;
};

type StoreHours = {
    FridayCloseTime: number;
    FridayOpenTime: number;
    IsClosedOnFriday: boolean;
    IsClosedOnMonday: boolean;
    IsClosedOnSaturday: boolean;
    IsClosedOnSunday: boolean;
    IsClosedOnThursday: boolean;
    IsClosedOnTuesday: boolean;
    IsClosedOnWednesday: boolean;
    MondayCloseTime: number;
    MondayOpenTime: number;
    OpenFrom: number;
    OpenTo: number;
    SaturdayCloseTime: number;
    SaturdayOpenTime: number;
    SundayCloseTime: number;
    SundayOpenTime: number;
    ThursdayCloseTime: number;
    ThursdayOpenTime: number;
    TuesdayCloseTime: number;
    TuesdayOpenTime: number;
    WednesdayCloseTime: number;
    WednesdayOpenTime: number;
};

export class GetFullStoreLocationsWithAvailabilitiesInput implements Msdyn365.IActionInput {
    constructor() {}

    public getCacheKey = () => `GetFullStoreLocationsWithAvailabilitiesInput`;
    public getCacheObjectType = () => 'GetFullStoreLocationsWithAvailabilitiesInput';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetFullStoreLocationsWithAvailabilitiesData {
    Locations: Location[];
    SelectedId?: string;
    AzureMapsApiKey?: string;
}

const createInput = (ctx: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetFullStoreLocationsWithAvailabilitiesInput();
};

async function action(
    input: GetFullStoreLocationsWithAvailabilitiesInput,
    ctx: Msdyn365.IActionContext
): Promise<IGetFullStoreLocationsWithAvailabilitiesData> {
    const { url } = ctx.requestContext.app.config.locationsDataSource;
    const AzureMapsApiKey = ctx.requestContext.app.config.azureMapsApiKey;
    const { data } = await Msdyn365.sendRequest<string>(url, 'get');
    const base = await parseDataSource(data.trim());
    const contactGrouped = groupByContact(base);
    const processed = (groupByHour(contactGrouped) as unknown) as any[];
    const result = processed.map(({ SquareFootage, Longitude, Latitude, StoreTollFree, StorePhone, StoreFax, ...rest }) => {
        const longitude = parseFloat(Longitude as string);
        const latitude = parseFloat(Latitude as string);
        const squareFootage = parseFloat(SquareFootage as string);
        return {
            ...rest,
            Id: uniqueId(),
            SquareFootage: isNaN(squareFootage) ? undefined : squareFootage,
            Longitude: isNaN(longitude) ? undefined : longitude,
            Latitude: isNaN(latitude) ? undefined : latitude,
            Phones: [
                { Type: 'TollFree', Value: StoreTollFree },
                { Type: 'Phone', Value: StorePhone },
                { Type: 'Fax', Value: StoreFax }
            ]
        } as Location;
    });
    return { Locations: result, AzureMapsApiKey };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetFullStoreLocationsWithAvailabilitiesData>>action,
    id: 'GetFullStoreLocationsWithAvailabilities',
    input: createInput
});

async function parseDataSource(csv: string): Promise<IGetFullStoreLocationsWithAvailabilitiesData[]> {
    return new Promise(resolve => {
        readString(csv, { complete: (records: any) => resolve(records.data), header: true });
    });
}

function groupByContact(base: any[]) {
    const list = base.map(obj => Object.entries(obj));
    const contactEntries = list.map(entries => {
        const temp = entries
            .filter(([key]) => /ContactName|ContactTitle/.test(key))
            .map(([key, value]) => [key.match(/\d*$/)?.[0], key.replace(/Contact|\d*/g, ''), value]);
        const grouped = groupBy(temp, (entries: [string]) => entries[0]);
        const groupedEntries = Object.values(grouped).map((element: any) =>
            Object.fromEntries(element.map((arr: any[]) => [arr[1], arr[2]]))
        );
        return groupedEntries;
    });
    const nonContacts = base.map(item => {
        const entries = Object.entries(item).filter(([key]) => !/ContactName|ContactTitle/.test(key));
        return Object.fromEntries(entries);
    });
    return nonContacts.map((item, i) => ({ ...item, Contacts: contactEntries[i] }));
}

function groupByHour(base: any[]) {
    const isHourField = (key: string) => /OpenFrom|OpenTo|IsClose|CloseTime|OpenTime/i.test(key.trim());

    return base.map(obj => {
        const hourEntries = {};
        const nonHourEntries = {};

        Object.entries(obj).forEach(([key, value]) => {
            if (isHourField(key)) {
                hourEntries[key] = /isclose/i.test(key) ? !!value : isNaN(parseInt(String(value))) ? null : parseInt(String(value));
            } else {
                nonHourEntries[key] = value;
            }
        });

        return { ...nonHourEntries, Hours: hourEntries };
    });
}
